import { Helmet } from "react-helmet-async";
import { Suspense, lazy } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Footer from "./components/footer/Footer";
import Navbar from "./components/navbar/Navbar";
import { useAuth } from "./context/AuthContext";
import { useTokenRefresh } from "./hooks/useTokenRefresh";
import ScrollToTopButton from "./ScrollToTopButton";
import ErrorBoundary from "./components/common/ErrorBoundary";
import HomePage from "./components/homePage/HomePage";

const Feeds = lazy(() => import("./components/pages/feeds/Feeds"));
const Registration = lazy(() => import("./components/pages/registration/Registration"));

const SwagramaRefundPolicy = lazy(
  () => import("./components/pages/refund-policy/RefundPolicy"),
);

const ServicesTabs = lazy(
  () => import("./components/pages/healingServices/ServicesTabs"),
);
const CommunityMembership = lazy(
  () =>
    import("./components/pages/membership/communityMembership/CommunityMembership"),
);
const CommunityActivitiesTabs = lazy(
  () =>
    import("./components/pages/communityActivities/CommunityActivitiesTabs"),
);
const CommuneTabs = lazy(() => import("./components/pages/commune/Commune"));
const EShop = lazy(() => import("./components/pages/eShop/EShop"));
const EventCalander = lazy(
  () => import("./components/pages/eventsCalander/EventCalander"),
);
const AboutUs = lazy(() => import("./components/pages/aboutUs/AboutUs"));
const UserDashboard = lazy(
  () => import("./components/pages/dashboard/UserDashboard"),
);
const SwagramaPrivacyPolicy = lazy(
  () => import("./components/pages/privacypolicy/PrivacyPolicyNew"),
);
const TermsAndConditon = lazy(
  () => import("./components/pages/terms&conditon/TermsAndConditon"),
);
const AdminLayout = lazy(() => import("./components/pages/admin/AdminLayout"));

function PageSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 animate-pulse">
      <div className="h-[260px] bg-stone-200/80 rounded-2xl w-full" />
      <div className="h-12 bg-stone-200/80 rounded-lg mt-4 w-full" />
      <div className="h-8 bg-stone-200/80 rounded-lg mt-2 w-3/5" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="h-[180px] bg-stone-200/80 rounded-2xl" />
        <div className="h-[180px] bg-stone-200/80 rounded-2xl" />
        <div className="h-[180px] bg-stone-200/80 rounded-2xl" />
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const { user: userData } = useAuth();
  useTokenRefresh();

  return (
    <div className=" font-inter ">
      <Helmet>
        <title>SwaGrama | Ayurveda • Yoga • Nisarga • Agro • Tourism</title>
        <meta
          name="description"
          content="SwaGrama is a self-sufficient village connected to cultural roots, embracing the traditional Indian science of health - Ayurveda and Yoga."
        />
      </Helmet>
      <Navbar />
      <div className="pt-16 md:pt-20 bg-gradient-to-br from-[#FFF8D6]/60 via-[#F1FFF5]/70 to-[#FFF9F1]/60 outline-none 2xl:mt-8">
        <ErrorBoundary>
          <Suspense fallback={<PageSkeleton />}>
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<HomePage userData={userData} />} />
              <Route
                path="/healing"
                element={<ServicesTabs userData={userData} />}
              />
              <Route
                path="/membership"
                element={<CommunityMembership userData={userData} />}
              />
              <Route
                path="/community-activities"
                element={<CommunityActivitiesTabs userData={userData} />}
              />
              <Route
                path="/commune"
                element={<CommuneTabs userData={userData} />}
              />
              <Route path="/eShop" element={<EShop userData={userData} />} />
              <Route
                path="/calendar"
                element={<EventCalander userData={userData} />}
              />
              <Route path="/aboutUs" element={<AboutUs />} />
              <Route
                path="/termsAndConditions"
                element={<TermsAndConditon />}
              />
              <Route
                path="/privacyPolicy"
                element={<SwagramaPrivacyPolicy />}
              />
              <Route path="refundPolicy" element={<SwagramaRefundPolicy />} />
              <Route path="feeds" element={<Feeds />} />
              <Route path="/dashboard" element={<UserDashboard />} />
              <Route path="/admin/dashboard" element={<AdminLayout />} />
              <Route path="/registration" element={<Registration />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
        <ScrollToTopButton />
      </div>

      <Footer />
    </div>
  );
}
